import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, FileText, Calendar, MessageSquare, Plus, Edit, Trash2, 
  Send, ChevronDown, ChevronRight, Clock, DollarSign,
  MapPin, User, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MemberManagement = () => {
  // State for all data
  const [members, setMembers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('quotes');
  
  // Selected member for filtering
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'quote', 'itinerary', 'message'
  const [editingItem, setEditingItem] = useState(null);
  
  // Message reply
  const [replyContent, setReplyContent] = useState('');
  const [activeConversation, setActiveConversation] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [membersRes, quotesRes, itinerariesRes, messagesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/members`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/quotes`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/itineraries`, { withCredentials: true }),
        axios.get(`${API_URL}/admin/messages`, { withCredentials: true })
      ]);
      
      setMembers(membersRes.data.members || []);
      setQuotes(quotesRes.data.quotes || []);
      setItineraries(itinerariesRes.data.itineraries || []);
      setConversations(messagesRes.data.conversations || []);
    } catch (error) {
      console.error('Error loading member data:', error);
      toast.error('Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on selected member
  const filteredQuotes = selectedMember 
    ? quotes.filter(q => q.user_id === selectedMember.user_id)
    : quotes;
  
  const filteredItineraries = selectedMember
    ? itineraries.filter(i => i.user_id === selectedMember.user_id)
    : itineraries;
  
  const filteredConversations = selectedMember
    ? conversations.filter(c => c.user_id === selectedMember.user_id)
    : conversations;

  // Search members
  const searchedMembers = members.filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // CRUD Operations
  const handleCreateQuote = async () => {
    try {
      const res = await axios.post(`${API_URL}/admin/quotes`, editingItem, { withCredentials: true });
      setQuotes([res.data.quote, ...quotes]);
      setIsDialogOpen(false);
      toast.success('Quote created successfully');
    } catch (error) {
      toast.error('Failed to create quote');
    }
  };

  const handleUpdateQuote = async () => {
    try {
      const res = await axios.put(`${API_URL}/admin/quotes/${editingItem.quote_id}`, editingItem, { withCredentials: true });
      setQuotes(quotes.map(q => q.quote_id === editingItem.quote_id ? res.data.quote : q));
      setIsDialogOpen(false);
      toast.success('Quote updated successfully');
    } catch (error) {
      toast.error('Failed to update quote');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await axios.delete(`${API_URL}/admin/quotes/${quoteId}`, { withCredentials: true });
      setQuotes(quotes.filter(q => q.quote_id !== quoteId));
      toast.success('Quote deleted');
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const handleCreateItinerary = async () => {
    try {
      const res = await axios.post(`${API_URL}/admin/itineraries`, editingItem, { withCredentials: true });
      setItineraries([res.data.itinerary, ...itineraries]);
      setIsDialogOpen(false);
      toast.success('Itinerary created successfully');
    } catch (error) {
      toast.error('Failed to create itinerary');
    }
  };

  const handleUpdateItinerary = async () => {
    try {
      const res = await axios.put(`${API_URL}/admin/itineraries/${editingItem.itinerary_id}`, editingItem, { withCredentials: true });
      setItineraries(itineraries.map(i => i.itinerary_id === editingItem.itinerary_id ? res.data.itinerary : i));
      setIsDialogOpen(false);
      toast.success('Itinerary updated successfully');
    } catch (error) {
      toast.error('Failed to update itinerary');
    }
  };

  const handleDeleteItinerary = async (itineraryId) => {
    if (!window.confirm('Are you sure you want to delete this itinerary?')) return;
    try {
      await axios.delete(`${API_URL}/admin/itineraries/${itineraryId}`, { withCredentials: true });
      setItineraries(itineraries.filter(i => i.itinerary_id !== itineraryId));
      toast.success('Itinerary deleted');
    } catch (error) {
      toast.error('Failed to delete itinerary');
    }
  };

  const handleSendReply = async (userId) => {
    if (!replyContent.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/admin/messages/reply/${userId}`, { content: replyContent }, { withCredentials: true });
      
      // Check if conversation exists
      const existingConv = conversations.find(c => c.user_id === userId);
      
      if (existingConv) {
        // Update existing conversation
        setConversations(conversations.map(c => {
          if (c.user_id === userId) {
            return { ...c, messages: [...c.messages, res.data.message] };
          }
          return c;
        }));
      } else {
        // Create new conversation entry
        const member = members.find(m => m.user_id === userId);
        setConversations([{
          user_id: userId,
          user_name: member?.name || 'Unknown',
          user_email: member?.email || '',
          messages: [res.data.message],
          unread_count: 0
        }, ...conversations]);
      }
      
      setReplyContent('');
      toast.success('Reply sent');
    } catch (error) {
      console.error('Reply error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };

  const handleDeleteMessage = async (messageId, userId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/admin/messages/${messageId}`, { withCredentials: true });
      
      setConversations(conversations.map(c => {
        if (c.user_id === userId) {
          return { ...c, messages: c.messages.filter(m => m.message_id !== messageId) };
        }
        return c;
      }));
      
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  // Open dialog for create/edit
  const openDialog = (type, item = null) => {
    setDialogType(type);
    if (item) {
      setEditingItem({ ...item });
    } else {
      // Default values for new items
      if (type === 'quote') {
        setEditingItem({
          user_id: selectedMember?.user_id || '',
          destination: '',
          travelers: '',
          dates: '',
          estimated_price: '',
          status: 'pending',
          notes: ''
        });
      } else if (type === 'itinerary') {
        setEditingItem({
          user_id: selectedMember?.user_id || '',
          package_name: '',
          destination: '',
          start_date: '',
          end_date: '',
          status: 'confirmed',
          details: {},
          documents: []
        });
      } else if (type === 'message') {
        setEditingItem({
          user_id: selectedMember?.user_id || '',
          content: ''
        });
      }
    }
    setIsDialogOpen(true);
  };

  const handleSendNewMessage = async () => {
    if (!editingItem?.user_id || !editingItem?.content?.trim()) {
      toast.error('Please select a member and enter a message');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/admin/messages/reply/${editingItem.user_id}`, 
        { content: editingItem.content }, 
        { withCredentials: true }
      );
      
      // Check if conversation exists, if not create one
      const existingConv = conversations.find(c => c.user_id === editingItem.user_id);
      if (existingConv) {
        setConversations(conversations.map(c => {
          if (c.user_id === editingItem.user_id) {
            return { ...c, messages: [...c.messages, res.data.message] };
          }
          return c;
        }));
      } else {
        // Create new conversation entry
        const member = members.find(m => m.user_id === editingItem.user_id);
        setConversations([{
          user_id: editingItem.user_id,
          user_name: member?.name || 'Unknown',
          user_email: member?.email || '',
          messages: [res.data.message],
          unread_count: 0
        }, ...conversations]);
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      toast.success('Message sent to member');
    } catch (error) {
      console.error('Send message error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      converted: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#84bc00]"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-gray-900">Member Management</h2>
          <p className="text-gray-600">Manage quotes, itineraries, and messages for members</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge className="bg-gray-900 text-white">{members.length} Members</Badge>
          <Badge className="bg-yellow-500">{quotes.length} Quotes</Badge>
          <Badge className="bg-green-500 text-white">{itineraries.length} Itineraries</Badge>
          <Badge className="bg-blue-500 text-white">{conversations.reduce((acc, c) => acc + c.unread_count, 0)} Unread Messages</Badge>
        </div>
      </div>

      {/* Member Filter */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users size={20} />
            Select Member
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center flex-wrap">
            <div className="flex-1 min-w-[200px] max-w-md">
              <Select 
                value={selectedMember?.user_id || 'all'} 
                onValueChange={(v) => setSelectedMember(v === 'all' ? null : members.find(m => m.user_id === v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Members" />
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <Input 
                      placeholder="Type to search..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="mb-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <SelectItem value="all">All Members ({members.length})</SelectItem>
                  {searchedMembers.length === 0 ? (
                    <div className="p-2 text-sm text-gray-500">No members match "{searchTerm}"</div>
                  ) : (
                    searchedMembers.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        <div className="flex items-center gap-2">
                          <User size={14} />
                          <span>{m.name}</span>
                          <span className="text-gray-400 text-xs">({m.email})</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            {selectedMember && (
              <div className="flex items-center gap-2">
                <Badge className="bg-[#84bc00] text-white py-1 px-3">
                  Viewing: {selectedMember.name}
                </Badge>
                <Button variant="outline" size="sm" onClick={() => { setSelectedMember(null); setSearchTerm(''); }}>
                  <X size={14} className="mr-1" /> Clear
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="quotes" className="data-[state=active]:bg-[#84bc00] data-[state=active]:text-white">
            <FileText size={16} className="mr-2" />
            Quotes ({filteredQuotes.length})
          </TabsTrigger>
          <TabsTrigger value="itineraries" className="data-[state=active]:bg-[#84bc00] data-[state=active]:text-white">
            <Calendar size={16} className="mr-2" />
            Itineraries ({filteredItineraries.length})
          </TabsTrigger>
          <TabsTrigger value="messages" className="data-[state=active]:bg-[#84bc00] data-[state=active]:text-white">
            <MessageSquare size={16} className="mr-2" />
            Messages ({filteredConversations.reduce((acc, c) => acc + c.unread_count, 0)} unread)
          </TabsTrigger>
        </TabsList>

        {/* QUOTES TAB */}
        <TabsContent value="quotes" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">Member Quotes</h3>
            <Button onClick={() => openDialog('quote')} className="bg-[#84bc00] hover:bg-[#6a9600]">
              <Plus size={16} className="mr-2" />
              New Quote
            </Button>
          </div>
          
          {filteredQuotes.length === 0 ? (
            <Card className="text-center py-12 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No quotes found. Create one to get started.</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredQuotes.map(quote => (
                <Card key={quote.quote_id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <User size={18} className="text-gray-400" />
                          <span className="font-semibold">{quote.user_name}</span>
                          <span className="text-gray-500 text-sm">{quote.user_email}</span>
                          {getStatusBadge(quote.status)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Destination</p>
                            <p className="font-medium flex items-center gap-1">
                              <MapPin size={14} /> {quote.destination}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Travelers</p>
                            <p className="font-medium">{quote.travelers}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Dates</p>
                            <p className="font-medium">{quote.dates || 'Flexible'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Est. Price</p>
                            <p className="font-medium text-[#84bc00]">
                              {quote.estimated_price ? `$${quote.estimated_price.toLocaleString()}` : 'TBD'}
                            </p>
                          </div>
                        </div>
                        {quote.notes && (
                          <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-2 rounded">{quote.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button size="sm" variant="outline" onClick={() => openDialog('quote', quote)}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteQuote(quote.quote_id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ITINERARIES TAB */}
        <TabsContent value="itineraries" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">Member Itineraries</h3>
            <Button onClick={() => openDialog('itinerary')} className="bg-[#84bc00] hover:bg-[#6a9600]">
              <Plus size={16} className="mr-2" />
              New Itinerary
            </Button>
          </div>
          
          {filteredItineraries.length === 0 ? (
            <Card className="text-center py-12 text-gray-500">
              <Calendar size={48} className="mx-auto mb-4 opacity-50" />
              <p>No itineraries found. Create one to get started.</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredItineraries.map(itin => (
                <Card key={itin.itinerary_id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <User size={18} className="text-gray-400" />
                          <span className="font-semibold">{itin.user_name}</span>
                          <span className="text-gray-500 text-sm">{itin.user_email}</span>
                          {getStatusBadge(itin.status)}
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 mt-2">{itin.package_name}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Destination</p>
                            <p className="font-medium flex items-center gap-1">
                              <MapPin size={14} /> {itin.destination}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Start Date</p>
                            <p className="font-medium">{itin.start_date}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">End Date</p>
                            <p className="font-medium">{itin.end_date}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button size="sm" variant="outline" onClick={() => openDialog('itinerary', itin)}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteItinerary(itin.itinerary_id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* MESSAGES TAB */}
        <TabsContent value="messages" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">Member Conversations</h3>
            <Button onClick={() => openDialog('message')} className="bg-[#84bc00] hover:bg-[#6a9600]">
              <Plus size={16} className="mr-2" />
              New Message
            </Button>
          </div>
          
          {filteredConversations.length === 0 ? (
            <Card className="text-center py-12 text-gray-500">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
              <p>No conversations yet. Start one by clicking "New Message".</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredConversations.map(conv => (
                <Card key={conv.user_id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setActiveConversation(activeConversation === conv.user_id ? null : conv.user_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User size={20} className="text-gray-500" />
                        </div>
                        <div>
                          <p className="font-semibold">{conv.user_name}</p>
                          <p className="text-sm text-gray-500">{conv.user_email}</p>
                        </div>
                        {conv.unread_count > 0 && (
                          <Badge className="bg-red-500 text-white">{conv.unread_count} new</Badge>
                        )}
                      </div>
                      {activeConversation === conv.user_id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </div>
                    
                    {activeConversation === conv.user_id && (
                      <div className="mt-4 border-t pt-4">
                        {/* Messages */}
                        <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                          {conv.messages.map(msg => (
                            <div 
                              key={msg.message_id}
                              className={`flex ${msg.sender === 'company' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div 
                                className={`max-w-[70%] p-3 rounded-lg ${
                                  msg.sender === 'company' 
                                    ? 'bg-[#84bc00] text-white' 
                                    : 'bg-gray-100 text-gray-900'
                                }`}
                              >
                                <p>{msg.content}</p>
                                <div className="flex items-center justify-between mt-1">
                                  <p className={`text-xs ${msg.sender === 'company' ? 'text-white/70' : 'text-gray-500'}`}>
                                    {new Date(msg.created_at).toLocaleString()}
                                  </p>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className={`h-6 w-6 p-0 ${msg.sender === 'company' ? 'text-white/70 hover:text-white' : 'text-gray-400 hover:text-red-500'}`}
                                    onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.message_id, conv.user_id); }}
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Reply input */}
                        <div className="flex gap-2">
                          <Input 
                            placeholder="Type your reply..." 
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendReply(conv.user_id)}
                          />
                          <Button 
                            onClick={() => handleSendReply(conv.user_id)}
                            className="bg-[#84bc00] hover:bg-[#6a9600]"
                          >
                            <Send size={16} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'message' 
                ? 'New Message' 
                : `${editingItem?.quote_id || editingItem?.itinerary_id ? 'Edit' : 'Create'} ${dialogType === 'quote' ? 'Quote' : 'Itinerary'}`
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Member Selection - shown for all dialog types */}
            <div>
              <Label className="font-bold">Member *</Label>
              <Select 
                value={editingItem?.user_id || ''} 
                onValueChange={(v) => setEditingItem({...editingItem, user_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a member" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.name} ({m.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* MESSAGE DIALOG */}
            {dialogType === 'message' && (
              <>
                <div>
                  <Label className="font-bold">Message *</Label>
                  <Textarea 
                    value={editingItem?.content || ''} 
                    onChange={(e) => setEditingItem({...editingItem, content: e.target.value})}
                    placeholder="Type your message to the member..."
                    rows={4}
                    data-testid="new-message-textarea"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSendNewMessage}
                    className="bg-[#84bc00] hover:bg-[#6a9600]"
                    data-testid="send-new-message-btn"
                  >
                    <Send size={16} className="mr-2" />
                    Send Message
                  </Button>
                </div>
              </>
            )}

            {dialogType === 'quote' && (
              <>
                <div>
                  <Label className="font-bold">Destination *</Label>
                  <Input 
                    value={editingItem?.destination || ''} 
                    onChange={(e) => setEditingItem({...editingItem, destination: e.target.value})}
                    placeholder="e.g., Costa Rica"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold">Travelers *</Label>
                    <Input 
                      value={editingItem?.travelers || ''} 
                      onChange={(e) => setEditingItem({...editingItem, travelers: e.target.value})}
                      placeholder="e.g., 2 adults"
                    />
                  </div>
                  <div>
                    <Label className="font-bold">Dates</Label>
                    <Input 
                      value={editingItem?.dates || ''} 
                      onChange={(e) => setEditingItem({...editingItem, dates: e.target.value})}
                      placeholder="e.g., March 2026"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold">Estimated Price ($)</Label>
                    <Input 
                      type="number"
                      value={editingItem?.estimated_price || ''} 
                      onChange={(e) => setEditingItem({...editingItem, estimated_price: parseInt(e.target.value) || null})}
                      placeholder="e.g., 3500"
                    />
                  </div>
                  <div>
                    <Label className="font-bold">Status</Label>
                    <Select 
                      value={editingItem?.status || 'pending'} 
                      onValueChange={(v) => setEditingItem({...editingItem, status: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="converted">Converted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="font-bold">Notes</Label>
                  <Textarea 
                    value={editingItem?.notes || ''} 
                    onChange={(e) => setEditingItem({...editingItem, notes: e.target.value})}
                    placeholder="Internal notes about this quote..."
                    rows={3}
                  />
                </div>
              </>
            )}

            {dialogType === 'itinerary' && (
              <>
                <div>
                  <Label className="font-bold">Package/Trip Name *</Label>
                  <Input 
                    value={editingItem?.package_name || ''} 
                    onChange={(e) => setEditingItem({...editingItem, package_name: e.target.value})}
                    placeholder="e.g., Costa Rica Volcano & Surf"
                  />
                </div>
                <div>
                  <Label className="font-bold">Destination *</Label>
                  <Input 
                    value={editingItem?.destination || ''} 
                    onChange={(e) => setEditingItem({...editingItem, destination: e.target.value})}
                    placeholder="e.g., Costa Rica"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-bold">Start Date *</Label>
                    <Input 
                      type="date"
                      value={editingItem?.start_date || ''} 
                      onChange={(e) => setEditingItem({...editingItem, start_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="font-bold">End Date *</Label>
                    <Input 
                      type="date"
                      value={editingItem?.end_date || ''} 
                      onChange={(e) => setEditingItem({...editingItem, end_date: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-bold">Status</Label>
                  <Select 
                    value={editingItem?.status || 'confirmed'} 
                    onValueChange={(v) => setEditingItem({...editingItem, status: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Footer buttons for Quote and Itinerary only */}
            {(dialogType === 'quote' || dialogType === 'itinerary') && (
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={() => {
                    if (dialogType === 'quote') {
                      editingItem.quote_id ? handleUpdateQuote() : handleCreateQuote();
                    } else {
                      editingItem.itinerary_id ? handleUpdateItinerary() : handleCreateItinerary();
                    }
                  }}
                  className="bg-[#84bc00] hover:bg-[#6a9600]"
                >
                  {editingItem?.quote_id || editingItem?.itinerary_id ? 'Update' : 'Create'}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberManagement;
